import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { COLORS, GRADIENTS, MOBILE, SPACE } from '@/nova/theme/tokens';

/* A marketing asset's stats sheet (opened from its composer's Stats pill, or its library card's figures): the period,
   the KPI tiles, the per-version (a landing page: per-link) breakdown, and the way to its players. */

/** The sheet header's text block: the sheet title, then which asset these stats are for. */
export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

/** Mirrors the default Sheet title (gradient text) so the custom header reads identically. */
export const HeaderTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

/** The asset's name under the title; one line, ellipsised when long. */
export const HeaderName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Vertical stack: the period selector, then the KPI grid or a state notice, then the breakdown. */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/** The period controls row (reuses the Affiliates PeriodSelector, which lays out its own pills + date fields). */
export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** A small caption under the controls: which links / range this covers. */
export const Caption = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
  margin: 0;
`;

/** Responsive KPI grid. */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${SPACE[8]};

  ${MOBILE} {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

/** One KPI card: a muted label above a large value. */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  padding: ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
`;

export const CardLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

export const CardValue = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 22px;
  font-weight: 700;
  color: ${COLORS.text};
  line-height: 1.1;
`;

/** A quiet line under a KPI card's value (why a figure is missing). */
export const CardNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

/** Skeleton placeholder cards while the report loads (a KPI card's height). */
export const SkeletonCard = styled.div`
  height: 70px;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

/** The per-version (per-link) breakdown: a title over a compact table. */
export const Breakdown = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const BreakdownTitle = styled(Text).attrs({ tag: 'h3' as const })`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: ${COLORS.text};
`;

/** The breakdown table: one row per link, the figures right-aligned. */
export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${COLORS.hairline};
  border-radius: 12px;
  overflow: hidden;
  font-size: 13px;
  table-layout: auto;
`;

export const HeadCell = styled.th<{ $num?: boolean }>`
  padding: ${SPACE[8]} ${SPACE[12]};
  text-align: ${({ $num }) => ($num ? 'right' : 'left')};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  white-space: nowrap;
  background: ${COLORS.surfaceInset};

  ${MOBILE} {
    padding: ${SPACE[8]};
  }
`;

export const Row = styled.tr`
  background: ${COLORS.surfaceFaint};
`;

export const Cell = styled.td<{ $num?: boolean }>`
  padding: ${SPACE[8]} ${SPACE[12]};
  border-top: 1px solid ${COLORS.hairline};
  text-align: ${({ $num }) => ($num ? 'right' : 'left')};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $num }) => ($num ? 700 : 500)};
  color: ${COLORS.text};
  white-space: nowrap;

  ${MOBILE} {
    padding: ${SPACE[8]};
  }
`;

/** A row's label cell: takes the room left, ellipsised. */
export const LabelCell = styled(Cell)`
  width: 100%;
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** A row's asset name as a deep link to that asset (a landing page's per-link breakdown): the row's own text weight,
 *  underlined faintly until hovered, like "See players". */
export const AssetLink = styled.a`
  color: ${COLORS.text};
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;

  &:hover,
  &:focus-visible {
    color: ${COLORS.accent};
    text-decoration-color: currentColor;
  }
`;

/** The row's kind, before its name (a landing page's per-link breakdown). */
export const RowKind = styled(Text).attrs({ tag: 'span' as const })`
  margin-right: ${SPACE[4]};
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/** "See players": the Players page filtered to the asset's link(s) (a real address, opened in the app on a click),
 *  right-aligned with the breakdown's per-link "See players" (the table cell's inset plus its 1px border). */
export const PlayersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: baseline;
  column-gap: ${SPACE[12]};
  row-gap: ${SPACE[4]};
  padding-right: calc(${SPACE[12]} + 1px);
  font-size: 13px;

  ${MOBILE} {
    padding-right: calc(${SPACE[8]} + 1px);
  }
`;

/** A deep link to the Players page. */
export const PlayersLink = styled.a`
  font-weight: 600;
  color: ${COLORS.text};
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    color: ${COLORS.accent};
    text-decoration-color: currentColor;
  }
`;
